import SESDK from "../vendor/solarengine/web-cn-sesdk-es";

const SDK_VERSION = "1.3.5";
const PLACEHOLDER_APP_KEY = "__SOLARENGINE_APP_KEY__";
const PLACEHOLDER_USER_CODE = "__SOLARENGINE_USER_CODE__";
const MAX_QUEUE_LENGTH = 50;
const AD_PARAMS_STORAGE_KEY = "solarengine_ad_params";
const AD_PARAM_KEYS = ["adid", "clickid", "creativeid", "creativetype"];

let initialized = false;
let ready = false;
let currentAppMode = "pc";
let pendingEvents = [];

function isBrowser() {
    return typeof window !== "undefined" && typeof document !== "undefined";
}

function envEnabled(value) {
    return String(value || "").toLowerCase() === "true";
}

function hasValidConfig() {
    const appKey = process.env.REACT_APP_SOLARENGINE_APP_KEY;
    const userCode = process.env.REACT_APP_SOLARENGINE_USER_CODE;

    return Boolean(appKey)
        && Boolean(userCode)
        && appKey !== PLACEHOLDER_APP_KEY
        && userCode !== PLACEHOLDER_USER_CODE;
}

function shouldEnableSolarEngine() {
    return isBrowser()
        && envEnabled(process.env.REACT_APP_SOLARENGINE_ENABLED)
        && hasValidConfig();
}

function getDistinctIDCtx() {
    if (!envEnabled(process.env.REACT_APP_SOLARENGINE_DISTINCT_ID_CTX_ENABLED)) return "";

    const params = new URLSearchParams(window.location.search || "");
    return params.get("setDistinctIDCtx") || "";
}

function getAdParamsFromUrl() {
    const params = new URLSearchParams(window.location.search || "");
    return AD_PARAM_KEYS.reduce(function (result, key) {
        const value = params.get(key);
        if (value) result[key] = value;
        return result;
    }, {});
}

function getStoredAdParams() {
    try {
        return JSON.parse(window.localStorage.getItem(AD_PARAMS_STORAGE_KEY) || "{}");
    } catch (error) {
        return {};
    }
}

function saveAdParamsFromUrl() {
    const adParams = getAdParamsFromUrl();
    if (Object.keys(adParams).length <= 0) return;

    try {
        window.localStorage.setItem(AD_PARAMS_STORAGE_KEY, JSON.stringify(adParams));
    } catch (error) {
        // Ignore localStorage failures in private browsing or restricted webviews.
    }
}

function getAdProperties() {
    return {
        ...getStoredAdParams(),
        ...getAdParamsFromUrl()
    };
}

export function getSolarEngineAdParams() {
    const adProperties = isBrowser() ? getAdProperties() : {};
    return AD_PARAM_KEYS.reduce(function (result, key) {
        result[key] = adProperties[key] || "";
        return result;
    }, {});
}

function getCommonProperties() {
    return {
        site: "webfront",
        app_mode: currentAppMode,
        page_path: window.location.pathname || "",
        page_hash: window.location.hash || "",
        sdk_version: SDK_VERSION
    };
}

function normalizeProperties(properties) {
    const mergedProperties = {
        ...getCommonProperties(),
        ...getAdProperties(),
        ...(properties || {})
    };

    return Object.keys(mergedProperties).reduce(function (result, key) {
        if (mergedProperties[key] !== undefined && mergedProperties[key] !== null) {
            result[key] = mergedProperties[key];
        }
        return result;
    }, {});
}

function setSuperProperties() {
    if (!SESDK || typeof SESDK.setSuperProperties !== "function" || !isBrowser()) return;
    try {
        SESDK.setSuperProperties({
            ...getCommonProperties(),
            ...getAdProperties()
        });
    } catch (error) {
        // SolarEngine support can still inspect manual event properties if this fails.
    }
}

function sendEvent(eventName, properties) {
    if (!ready || !SESDK || typeof SESDK.track !== "function") return;
    try {
        SESDK.track(eventName, normalizeProperties(properties));
    } catch (error) {
        // Do not let analytics failures affect the site experience.
    }
}

function flushEvents() {
    const events = pendingEvents;
    pendingEvents = [];
    events.forEach(function (event) {
        sendEvent(event.eventName, event.properties);
    });
}

export function initSolarEngine({appMode = "pc"} = {}) {
    currentAppMode = appMode;   
    if (isBrowser()) saveAdParamsFromUrl();  
    if (initialized || !shouldEnableSolarEngine()) return;
 
    initialized = true;
    const appKey = process.env.REACT_APP_SOLARENGINE_APP_KEY;
    const userCode = process.env.REACT_APP_SOLARENGINE_USER_CODE;

    try {
        window.SESDK = SESDK;
        SESDK.prevInit(appKey);
        SESDK.init({
            appKey,
            userId: userCode,
            config: {
                debugModel: envEnabled(process.env.REACT_APP_SOLARENGINE_DEBUG_MODEL),
                logEnabled: envEnabled(process.env.REACT_APP_SOLARENGINE_LOG_ENABLED),
                isInApp: envEnabled(process.env.REACT_APP_SOLARENGINE_IS_IN_APP),
                setDistinctIDCtx: getDistinctIDCtx(),
                enableDistinctIDCtx: envEnabled(process.env.REACT_APP_SOLARENGINE_DISTINCT_ID_CTX_ENABLED)
            }
        });

        SESDK.ready(function () {
            ready = true;
            setSuperProperties();
            flushEvents();
        });
    } catch (error) {
        ready = false;
    }
}

export function setSolarEngineAppMode(appMode) {
    if (currentAppMode === appMode) return;
    currentAppMode = appMode;
    if (ready) setSuperProperties();
}

export function trackSolarEvent(eventName, properties = {}) {
    if (!shouldEnableSolarEngine() || !eventName) return;

    if (!ready) {
        pendingEvents.push({eventName, properties});
        if (pendingEvents.length > MAX_QUEUE_LENGTH) pendingEvents = pendingEvents.slice(-MAX_QUEUE_LENGTH);
        return;
    }

    sendEvent(eventName, properties);
}
